import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MAINTENANCE_MESSAGE, isMaintenanceStatus, toFriendlyErrorMessage } from "../../../utils/network";

const serverUrl = process.env.REACT_APP_SERVER_HOST;
const TOKEN_KEY = "auth_token";

const buildHeaders = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
};

const normalizeQuestionData = (item) => {
  if (!item || typeof item !== "object") return null;

  const questionId = Number(item.questionId);
  if (!Number.isFinite(questionId)) return null;

  return {
    questionId,
    questionText: typeof item.questionText === "string" ? item.questionText : "",
    isMcq: Boolean(item.isMcq),
    options: Array.isArray(item.options) ? item.options.filter((opt) => typeof opt === "string") : [],
    submittedAnswer: typeof item.submittedAnswer === "string" ? item.submittedAnswer : "",
    correctAnswer: typeof item.correctAnswer === "string" ? item.correctAnswer : "",
    isCorrect: Boolean(item.isCorrect),
  };
};

const normalizeQuestionGroupData = (item, index) => {
  if (!item || typeof item !== "object") return null;

  const questions = Array.isArray(item.questions)
    ? item.questions.map(normalizeQuestionData).filter(Boolean)
    : [];

  return {
    questionGroupId: item.questionGroupId,
    title: typeof item.title === "string" ? item.title : null,
    groupText: typeof item.groupText === "string" ? item.groupText : null,
    instructionText: typeof item.instructionText === "string" ? item.instructionText : null,
    groupType: typeof item.groupType === "string" ? item.groupType : null,
    questions,
  };
};

const TestResultPage = () => {
  const { testId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const attemptId = state?.attemptId;
  const result = state?.result;

  const [questionGroups, setQuestionGroups] = useState([]);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Session expired. Please log in again.");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    }
  }, [navigate]);

  const fetchTestResults = useCallback(async () => {
    const headers = buildHeaders();
    if (!headers) {
      setError("Authentication context is empty. Please log in again.");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
      return;
    }

    try {
      const url = new URL(`${serverUrl}/app/tests/${testId}/result`);
      if (attemptId) {
        url.searchParams.append("attemptId", attemptId);
      }

      console.log('Fetching test results from:', url.toString());
      console.log('Attempt ID:', attemptId);
      console.log('Headers:', headers);

      const response = await fetch(url.toString(), { headers });

      console.log('📊 Response Status:', response.status);
      console.log('📋 Response Headers:');
      console.log('  Content-Type:', response.headers.get('content-type'));
      console.log('  Content-Length:', response.headers.get('content-length'));
      console.log('  Content-Encoding:', response.headers.get('content-encoding'));
      console.log('  Access-Control-Allow-Origin:', response.headers.get('access-control-allow-origin'));

      if (response.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setError("Your session has expired. Please log in again.");
        setTimeout(() => {
          navigate("/login");
        }, 1500);
        return;
      }

      if (isMaintenanceStatus(response.status)) {
        throw new Error(MAINTENANCE_MESSAGE);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to load test results (${response.status}): ${errorText}`);
      }

      const responseText = await response.text();
      console.log('📝 Raw Response Text Length:', responseText.length);
      console.log('📝 Raw Response (first 500 chars):', responseText.substring(0, 500));
      console.log('📝 Raw Response (last 500 chars):', responseText.substring(responseText.length - 500));

      const data = JSON.parse(responseText);
      console.log('✅ Parsed JSON:', JSON.stringify(data, null, 2));

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format from server");
      }

      const { durationMinutes, questions } = data;
      if (!Array.isArray(questions)) {
        throw new Error("Questions data is missing or invalid");
      }

      setDurationMinutes(Number(durationMinutes) || 0);

      const groups = questions
        .map((group, index) => normalizeQuestionGroupData(group, index))
        .filter(Boolean);

      console.log('✅ After normalization:', JSON.stringify(groups, null, 2));
      console.log('✅ First question submittedAnswer:', groups[0]?.questions[0]?.submittedAnswer);

      setQuestionGroups(groups);
      setError(null);
    } catch (err) {
      console.error('Test results fetch error:', err);
      const errorMessage = toFriendlyErrorMessage(err, "Unable to load test results. Please try again.");
      setError(errorMessage);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [navigate, testId, attemptId]);

  useEffect(() => {
    fetchTestResults();
  }, [fetchTestResults]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#666",
        }}
      >
        Loading test results...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          padding: "20px",
        }}
      >
        <div
          style={{
            padding: "20px 30px",
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: "8px",
            border: "1px solid #fca5a5",
            textAlign: "center",
            maxWidth: "400px",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ margin: "0 0 10px", fontSize: "18px" }}>Error Loading Results</h3>
          <p style={{ margin: 0, fontSize: "14px" }}>{error}</p>
        </div>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            padding: "12px 24px",
            background: "#28a745",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const flattenedQuestions = questionGroups.flatMap((group) => group.questions || []);
  const correctCount = flattenedQuestions.filter((q) => q.isCorrect).length;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "0 16px 60px",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          zIndex: 10,
          padding: "16px 0",
          marginBottom: "26px",
        }}
      >
        <div
          style={{
            maxWidth: "760px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
             onClick={() => navigate("/dashboard")}
             style={{
               background: "#9ca3af",
               border: "none",
               padding: "7px 16px",
               borderRadius: "6px",
               cursor: "pointer",
               color: "#fff",
               fontSize: "14px",
               fontWeight: 600,
             }}
           >
             {'<-'} Back to Tests
           </button>
          <span
            style={{
              background: "#1a56db",
              color: "#fff",
              padding: "3px 12px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "capitalize",
              letterSpacing: "0.5px",
            }}
          >
            Test Review
          </span>
        </div>
      </div>

      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#111827", marginBottom: "8px" }}>
          Test Review
        </h2>

        {result && (
          <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "20px" }}>
            {result.testName} • Attempt #{result.attemptNumber}
          </p>
        )}

        {/* Summary Stats */}
        {flattenedQuestions.length > 0 && (
          <div
            style={{
              background: "#ffffff",
              border: "2px solid #1a56db",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "28px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "16px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: 800, color: "#1a56db", marginBottom: "6px" }}>
                {flattenedQuestions.length}
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280", fontWeight: 600 }}>Total Questions</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: 800, color: "#16a34a", marginBottom: "6px" }}>
                {correctCount}
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280", fontWeight: 600 }}>Correct</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: 800, color: "#dc3545", marginBottom: "6px" }}>
                {flattenedQuestions.length - correctCount}
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280", fontWeight: 600 }}>Incorrect</div>
            </div>
          </div>
        )}

        {/* Questions Display */}
        {flattenedQuestions.length === 0 ? (
          <p style={{ color: "#9ca3af", textAlign: "center", marginTop: "30px" }}>
            No questions found for this test.
          </p>
        ) : (
          <>
            {Array.isArray(questionGroups) && questionGroups.map((group) => (
             <section
               key={group?.questionGroupId || "no-group"}
               style={{
                 marginBottom: "20px",
               }}
             >
                {/* Group Header */}
                {group.title && (
                  <h3 style={{ margin: "0 0 8px", color: "#1f2937", fontSize: "18px", fontWeight: 700 }}>
                    {group.title}
                  </h3>
                )}
                {group.groupType && (
                  <div style={{ marginBottom: "8px", fontSize: "12px", color: "#6b7280", fontWeight: 600 }}>
                    Type: {group.groupType}
                  </div>
                )}
                {group.groupText && (
                  <p
                    style={{
                      margin: "0 0 12px",
                      color: "#374151",
                      lineHeight: 1.6,
                      fontSize: "14px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {group.groupText}
                  </p>
                )}
                {group.instructionText && (
                  <p style={{ margin: "0 0 16px", color: "#4b5563", fontStyle: "italic", fontSize: "13px" }}>
                    {group.instructionText}
                  </p>
                )}

                {/* Questions in this group */}
                <div>
                  {Array.isArray(group.questions) && group.questions.map((question, idx) => {
                    const isCorrect = question?.isCorrect;
                    const displayIndex = flattenedQuestions.findIndex((q) => q?.questionId === question?.questionId) + 1;

                    return (
                      <div
                        key={question?.questionId || idx}
                        style={{
                          border: `1px solid ${isCorrect ? "#dbeafe" : "#fee2e2"}`,
                          borderLeft: `4px solid ${isCorrect ? "#16a34a" : "#dc3545"}`,
                          borderRadius: "10px",
                          padding: "14px 16px",
                          marginBottom: idx === group.questions.length - 1 ? 0 : "12px",
                          background: isCorrect ? "#f0fdf4" : "#fffbfb",
                        }}
                      >
                        {/* Question Text */}
                        <div style={{ fontWeight: 700, color: "#1f2937", marginBottom: "12px", fontSize: "15px" }}>
                          {displayIndex}. {question.questionText}
                        </div>

                        {/* Multiple Choice Options - Show for reference if MCQ */}
                        {question.isMcq && question.options.length > 0 && (
                          <div style={{ marginBottom: "12px" }}>
                            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", fontWeight: 500 }}>
                              Options:
                            </div>
                            <div style={{ display: "grid", gap: "6px" }}>
                              {question.options.map((option) => (
                                <div
                                  key={option}
                                  style={{
                                    padding: "6px 10px",
                                    background: "rgba(0,0,0,0.03)",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    color: "#374151",
                                  }}
                                >
                                  • {option}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Answer Section */}
                        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid rgba(0,0,0,0.05)" }}>
                          {isCorrect ? (
                            <>
                              <div style={{ marginBottom: "10px" }}>
                                <strong style={{ color: "#1f2937", fontSize: "13px" }}>Your Answer:</strong>
                                <div
                                  style={{
                                    marginTop: "4px",
                                    padding: "8px 12px",
                                    background: "#dcfce7",
                                    border: "1px solid #86efac",
                                    borderRadius: "6px",
                                    color: "#166534",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                  }}
                                >
                                  {question.submittedAnswer || "Not answered"}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ marginBottom: "10px" }}>
                                <strong style={{ color: "#1f2937", fontSize: "13px" }}>Your Answer:</strong>
                                <div
                                  style={{
                                    marginTop: "4px",
                                    padding: "8px 12px",
                                    background: "#fee2e2",
                                    border: "1px solid #fca5a5",
                                    borderRadius: "6px",
                                    color: "#991b1b",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                  }}
                                >
                                  {question.submittedAnswer || "Not answered"}
                                </div>
                              </div>
                              <div>
                                <strong style={{ color: "#1f2937", fontSize: "13px" }}>Correct Answer:</strong>
                                <div
                                  style={{
                                    marginTop: "4px",
                                    padding: "8px 12px",
                                    background: "#dcfce7",
                                    border: "1px solid #86efac",
                                    borderRadius: "6px",
                                    color: "#166534",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                  }}
                                >
                                  {question.correctAnswer}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Status Badge */}
                        <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 12px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 600,
                              background: isCorrect ? "#d1fae5" : "#fee2e2",
                              color: isCorrect ? "#047857" : "#b91c1c",
                            }}
                          >
                            <span style={{ fontSize: "14px" }}>{isCorrect ? "✓" : "✗"}</span>
                            {isCorrect ? "Correct" : "Incorrect"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </>
        )}

        {/* Action Buttons */}
        <div style={{ textAlign: "center", marginTop: "30px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "12px 24px",
              background: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestResultPage;
